import { memo, useCallback, useMemo, useState } from "react";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";

import { useHandleFormState } from "./hooks";

type Props = {
	// 現地決済のキャンセル料金支払いを非承認する
	onUpdateReservationLocalCancelFeeTransferReject: (
		insufficientAmount: number,
		rejectReason: string,
	) => Promise<void>;
};

export const LocalCancelFeeTransferRejectModal = memo(({ onUpdateReservationLocalCancelFeeTransferReject }: Props) => {
	const {
		rejectReason,
		rejectReasonErrorMessage,
		insufficientAmount,
		insufficientAmountErrorMessage,
		hasError,
		//
		handleChangeRejectReason,
		handleChangeInsufficientAmount,
	} = useHandleFormState();

	const [canOpenModal, setCanOpenModal] = useState(false);
	/**
	 * モーダルを表示
	 */
	const handleClickOpenModal = useCallback(() => {
		setCanOpenModal(true);
	}, []);

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open) {
				handleChangeRejectReason("");
				handleChangeInsufficientAmount(undefined);
			}
			setCanOpenModal(open);
		},
		[handleChangeRejectReason, handleChangeInsufficientAmount],
	);

	/**
	 * 現地決済のキャンセル料金支払いを非承認する
	 */
	const handleClickUpdateReservationLocalCancelFeeTransferReject = useCallback(() => {
		if (hasError || insufficientAmount === undefined) return;

		onUpdateReservationLocalCancelFeeTransferReject(insufficientAmount, rejectReason).then(() =>
			setCanOpenModal(false),
		);
	}, [hasError, insufficientAmount, onUpdateReservationLocalCancelFeeTransferReject, rejectReason]);

	const disabled = useMemo(() => hasError, [hasError]);

	/**
	 * モーダルを表示するボタン
	 */
	if (!canOpenModal) {
		return (
			<Button variant="outline" type="button" size="sm" onClick={handleClickOpenModal}>
				非承認
			</Button>
		);
	}

	return (
		<Dialog open={canOpenModal} onOpenChange={handleOpenChange}>
			<DialogContent>
				<DialogHeader className="border-b border-b-zinc-200 pb-4">
					<DialogTitle>キャンセル料金支払いの非承認</DialogTitle>
				</DialogHeader>

				<div className="space-y-4 p-2">
					{/* 注意事項 */}
					<p className="whitespace-pre text-center">
						{`非承認をご記載ください。\n入力された文章のままお客様へ通知されます。`}
					</p>

					{/* 非承認理由 */}
					<TextAreaField
						label="非承認理由"
						value={rejectReason}
						handleChange={handleChangeRejectReason}
						errorMessage={rejectReasonErrorMessage}
					/>
				</div>

				{/* 不足金額 */}
				<div className="flex items-end gap-x-3">
					<InputNumberField
						label="不足金額"
						value={insufficientAmount}
						handleInput={handleChangeInsufficientAmount}
						errorMessage={insufficientAmountErrorMessage}
					/>
					<p>円</p>
				</div>

				<div className="flex justify-end">
					<Button
						type="button"
						size="xl"
						disabled={disabled}
						onClick={handleClickUpdateReservationLocalCancelFeeTransferReject}
					>
						送信
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
});
